import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  TablePagination,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LocalShipping as ReceiptIcon,
  ArrowBack as BackIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  CloudUpload as UploadIcon,
  CloudDownload as DownloadIcon,
  Description as DocIcon,
  DeleteOutline as RemoveIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PendingActions as PendingActionsIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import {
  getGoodsReceipts,
  getGoodsReceipt,
  createGoodsReceipt,
  createGoodsReceiptsSplit,
  acceptGoodsReceipt,
  rejectGoodsReceipt,
  updateQualityInspection,
  updateGoodsReceipt,
  updateGoodsReceiptBatchExpiry,
  uploadGoodsReceiptDocument,
  deleteGoodsReceiptDocument,
  deleteGoodsReceipt,
  getGoodsReceiptTimeline,
  getPurchaseOrders,
  getPurchaseOrder,
  formatCurrency,
  formatDate,
  formatNumber,
  getStatusColor
} from '../../services/procurementService';
import { getWarehouses } from '../../services/inventoryService';
import { EntityChip, StatusBadge, PageHeader, EmptyState, UploadZone, Timeline, ActionBar, SavedViews } from '../ds';
import { useListShortcuts } from '../common/GlobalShortcuts';
import ConfirmDialog from '../common/ConfirmDialog';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard';
import { useBrand } from '../../context/BrandContext';
import api from '../../config/api';
import { skippedCostRows, skippedCostRowsWarning } from '../../utils/cogsWriteFeedback';

// Backend handlers return either { error: '...' } (custom) or
// { errors: [{msg, path}, ...] } (express-validator). The plain-axios fallback
// shows the unhelpful "Request failed with status code 400" — this surfaces
// the actual validation message and field path.
// Audit D1: receipt provenance labels, mirroring the backend's prefix-derived
// receipt_type (all prefixes live-verified against production 2026-08-21).
const RECEIPT_TYPE_LABELS = {
  po_receipt: { label: 'PO receipt', color: 'primary' },
  factory_import: { label: 'Factory import', color: 'default' },
  opening_balance: { label: 'Opening balance', color: 'default' },
  supplier_ledger: { label: 'Supplier ledger', color: 'info' },
  correction: { label: 'Correction', color: 'warning' },
};

const extractApiError = (e, fallback = 'Request failed') => {
  const d = e?.response?.data;
  if (d?.error) return d.error;
  if (Array.isArray(d?.errors) && d.errors.length) {
    return d.errors.map(x => x.path ? `${x.msg} (${x.path})` : x.msg).join('; ');
  }
  return e?.message || fallback;
};

function GoodsReceipts() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedBrand } = useBrand();
  const poIdFromUrl = searchParams.get('po_id');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Non-blocking: the receipt was recorded but the server skipped some product cost rows (cost_rows_skipped, W20).
  const [warning, setWarning] = useState(null);

  // List view state — fully server-side paginated + filtered (was previously
  // a client-side .slice() over a 100-row prefetch; rows 101+ were invisible).
  const [receipts, setReceipts] = useState([]);
  const [totalReceipts, setTotalReceipts] = useState(0);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  // Local typing buffer — TextField writes here on every keystroke; a debounced
  // useEffect below mirrors to searchTerm (which drives the API fetch). Without
  // this, every letter triggered an immediate refetch + page-spinner unmount,
  // giving the "page reloads on every keystroke" feel.
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' = all
  // Audit D1: drafts/opening balances (affects_stock=0) hidden by default.
  const [showNonStock, setShowNonStock] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Debounce searchInput → searchTerm: 400ms after user stops typing.
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        setPage(0);
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Detail view state
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  // Inspection edit mode — when ON, per-line accepted/rejected/damage_reason are
  // editable. Save persists via updateQualityInspection (PUT /:id/quality) which
  // also stamps inspection_date + inspector_id and sets header status='completed'.
  const [inspectionEditMode, setInspectionEditMode] = useState(false);
  const [inspectionEdits, setInspectionEdits] = useState({}); // { [gr_item_id]: { accepted_quantity, rejected_quantity, damage_reason } }
  const [savingInspection, setSavingInspection] = useState(false);
  // Receipt-date edit — inline in the Receipt Details card. Persists via
  // PUT /:id with { receipt_date }, which also syncs this GR's batch dates
  // and the parent PO's actual_delivery_date server-side.
  const [editingReceiptDate, setEditingReceiptDate] = useState(false);
  const [receiptDateDraft, setReceiptDateDraft] = useState('');
  const [savingReceiptDate, setSavingReceiptDate] = useState(false);
  // Batch & expiry edit — a dedicated dialog, separate from the quantity/inspection
  // path so it works on any PO type and carries no quantity coupling. Persists via
  // PUT /:id/batch-expiry; backend guards batch-number changes on used stock.
  const [batchExpiryOpen, setBatchExpiryOpen] = useState(false);
  const [batchExpiryEdits, setBatchExpiryEdits] = useState({}); // { [gr_item_id]: { batch_number, expiry_date } }
  const [savingBatchExpiry, setSavingBatchExpiry] = useState(false);

  // Dialog state — start closed and only open after the linked PO data has
  // actually loaded. Avoids the flash of an empty dialog when the user arrives
  // via /procurement/goods-receipts?po_id=N before the fetch completes.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Shared MUI confirm (Delete Receipt / Remove Document). Was a local copy of
  // a confirm dialog that rendered differently from the app-wide one; now uses
  // the single shared primitive so every confirm in the app matches.
  const { confirmDialogProps, confirm } = useConfirmDialog();

  // Guard the create-receipt dialog — it holds a whole receipt's worth of
  // typed quantities, so a stray backdrop click was expensive.
  const [formTouched, setFormTouched] = useState(false);
  const guardedCloseDialog = useUnsavedChangesGuard(formTouched, confirm);
  // The dialog opens from several places (button, keyboard shortcut, ?po_id=
  // deep link); resetting here covers every one.
  useEffect(() => { if (dialogOpen) setFormTouched(false); }, [dialogOpen]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [receiptForm, setReceiptForm] = useState({
    po_id: poIdFromUrl || '',
    receipt_date: new Date().toISOString().split('T')[0],
    received_by: 1, // TODO: Use actual user ID
    notes: '',
    // Destination warehouse for this whole receipt. Defaults from PO.warehouse_code
    // when the PO is selected; operator can override. Per-line splits across
    // multiple destinations (Pattern B) are a deliberate follow-up that needs
    // a schema + stock-levels CTE change.
    warehouse_code: '',
    items: []
  });
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => {
    loadPurchaseOrders();
    if (id && id !== 'new') {
      // '/goods-receipts/new' is the create route (:id captures the literal
      // 'new'); it's not a real receipt id, so don't fetch its details/timeline
      // (that fired getGoodsReceipt('new') + timeline → 2× 400). The create
      // dialog is opened by the poIdFromUrl effect below.
      loadReceiptDetails(id);
    } else {
      loadReceipts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, selectedBrand?.id, page, rowsPerPage, statusFilter, searchTerm, showNonStock]);

  useEffect(() => {
    if (poIdFromUrl) {
      // Async chain: fetch PO, then open dialog. loadPOForReceipt populates
      // selectedPO + receiptForm.items so the dialog has data when it opens.
      (async () => {
        // DT-1922: clear a stale page error before the dialog opens (done before the load, so nothing the load sets is
        // wiped; loadPOForReceipt itself only logs a failed PO load).
        setError(null);
        await loadPOForReceipt(poIdFromUrl);
        setDialogOpen(true);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poIdFromUrl]);

  const loadPurchaseOrders = async () => {
    try {
      // Broader status filter — operator might record receipt against any PO
      // that's not draft/cancelled/closed. The old 'sent'-only filter made the
      // dropdown empty for any project that moved fast through statuses.
      const res = await getPurchaseOrders({ limit: 100 });
      const posData = res?.data;
      const all = Array.isArray(posData) ? posData : (posData?.purchase_orders || []);
      const receivable = all.filter(po => !['draft','cancelled','closed'].includes(String(po.status || '').toLowerCase()));
      setPurchaseOrders(receivable);
    } catch (err) {
      console.error('Failed to load POs:', err);
    }
    // Fire-and-forget warehouses load — feeds the header + per-line pickers
    // in the Record Goods Receipt dialog. Filters mirror PurchaseOrderEntry.js
    // (active + physical only — GIT etc. excluded).
    try {
      const wRes = await getWarehouses({ is_active: true, exclude_virtual: true });
      setWarehouses(wRes?.data || wRes?.warehouses || []);
    } catch (err) {
      console.warn('Failed to load warehouses:', err);
    }
  };

  const loadPOForReceipt = async (poId) => {
    try {
      const res = await getPurchaseOrder(poId);
      const po = res?.data;
      if (po) {
        setSelectedPO(po);
        const poWarehouseCodeRaw = po.warehouse_code || '';
        // GIT is a virtual accounting bucket — never auto-fill it as the
        // receipt destination. Operator must explicitly pick a real warehouse.
        // Alert below the picker surfaces the reason when this triggers.
        const poWarehouseCode = poWarehouseCodeRaw.toUpperCase() === 'GIT'
          ? ''
          : poWarehouseCodeRaw;
        setReceiptForm({
          ...receiptForm,
          po_id: poId,
          warehouse_code: poWarehouseCode,
          items: (po.items || []).map(item => {
            const ordered = Number(item.ordered_quantity ?? item.quantity ?? 0);
            const alreadyReceived = Number(item.received_quantity || 0);
            const remaining = Math.max(0, ordered - alreadyReceived);
            return ({
            po_item_id: item.id,
            sku: item.sku,
            product_name: item.product_name,
            ordered_quantity: ordered,
            // Surface what's already been received so the operator can see
            // context + the default for "this receipt" is the REMAINING qty,
            // not the full ordered qty (which would double-count on partials).
            already_received: alreadyReceived,
            remaining_quantity: remaining,
            received_quantity: remaining,
            accepted_quantity: remaining,
            rejected_quantity: 0,
            rejection_reason: '',
            // Per-line warehouse override. Empty = use the GR header value.
            // Setting a different value per line triggers the split endpoint.
            warehouse_code: '',
          });
          })
        });
      }
    } catch (err) {
      console.error('Failed to load PO:', err);
    }
  };

  const loadReceipts = async () => {
    setLoading(true);
    setError(null);

    try {
      // Note: Procurement operates at company level, not brand level.
      // Pagination + filters are now server-side; pass offset/limit + optional
      // status / search filters.
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      if (statusFilter) params.inspection_status = statusFilter;
      if (searchTerm && searchTerm.trim().length >= 2) params.search = searchTerm.trim();
      // Default view hides rows that never touch stock (factory-import drafts +
      // CIN7 opening balances); the toggle reveals them. Server-side so
      // pagination stays correct.
      if (!showNonStock) params.exclude_non_stock = true;

      const res = await getGoodsReceipts(params);
      const receiptsData = res?.data;
      // Backend returns { goods_receipts, total, limit, offset }
      setReceipts(Array.isArray(receiptsData) ? receiptsData : (receiptsData?.goods_receipts || []));
      setTotalReceipts(Number(receiptsData?.total) || 0);
    } catch (err) {
      console.error('Failed to load receipts:', err);
      setError('Failed to load goods receipts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Activity timeline for the current GR (loaded alongside detail).
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Only the newest receipt load may apply its answer: an older one landing late (the user opened another receipt, or went
  // back to the list) would show the wrong receipt under this URL (DT-1139, same as Transfers DT-1113).
  const detailRequestRef = useRef(0);
  useEffect(() => { if (!id || id === 'new') detailRequestRef.current += 1; }, [id]);

  const loadReceiptDetails = async (receiptId) => {
    const request = ++detailRequestRef.current;
    const stale = () => request !== detailRequestRef.current;
    // A different record: drop the previous one now, so a failed load never leaves it on screen under this URL.
    setSelectedReceipt(prev => (prev && String(prev.id) === String(receiptId) ? prev : null));
    setLoading(true);
    setError(null);
    setTimelineLoading(true);

    try {
      const res = await getGoodsReceipt(receiptId);
      if (stale()) return;
      setSelectedReceipt(res?.data);
      setEditingReceiptDate(false);
    } catch (err) {
      if (stale()) return;
      console.error('Failed to load receipt details:', err);
      setError('Failed to load receipt details. Please try again.');
    } finally {
      if (!stale()) setLoading(false);
    }

    // Timeline fetched in parallel — non-blocking; failure is silent (just leaves list empty).
    try {
      const tl = await getGoodsReceiptTimeline(receiptId);
      if (stale()) return;
      setTimelineEvents(tl?.data || []);
    } catch (err) {
      if (stale()) return;
      console.warn('Failed to load timeline:', err);
      setTimelineEvents([]);
    } finally {
      if (!stale()) setTimelineLoading(false);
    }
  };

  const handlePOChange = async (poId) => {
    setFormTouched(true); // MUI Select/Autocomplete emit no DOM change event
    setReceiptForm({ ...receiptForm, po_id: poId, items: [] });
    if (poId) {
      await loadPOForReceipt(poId);
    }
  };

  const handleUpdateItem = (index, field, value) => {
    setFormTouched(true); // MUI Select/Autocomplete emit no DOM change event
    const newItems = [...receiptForm.items];
    newItems[index] = { ...newItems[index], [field]: value };

    // Auto-calculate accepted quantity
    if (field === 'received_quantity' || field === 'rejected_quantity') {
      const received = field === 'received_quantity' ? parseFloat(value) || 0 : parseFloat(newItems[index].received_quantity) || 0;
      const rejected = field === 'rejected_quantity' ? parseFloat(value) || 0 : parseFloat(newItems[index].rejected_quantity) || 0;
      newItems[index].accepted_quantity = Math.max(0, received - rejected);
    }

    setReceiptForm({ ...receiptForm, items: newItems });
  };

  const handleSave = async () => {
    try {
      setError(null);
      // Detect whether the operator split this receipt across multiple
      // warehouses. Route to the split endpoint if yes, normal endpoint
      // otherwise. The server's split path falls back to a single-GR
      // call when items collapse to one warehouse, so this is also safe
      // as a default — but selecting the right client function keeps the
      // semantics obvious in network logs / dev tools.
      const headerWh = receiptForm.warehouse_code || '';
      const resolvedPerLine = (receiptForm.items || [])
        .filter(it => Number(it.received_quantity) > 0)
        .map(it => (it.warehouse_code || headerWh));
      const isSplit = new Set(resolvedPerLine.filter(Boolean)).size > 1;
      // Build payload — only include per-line warehouse_code when it
      // differs from the header. Keeps payloads tight and lets the
      // server use header as the single source of truth otherwise.
      const payload = {
        ...receiptForm,
        items: receiptForm.items.map(it => {
          const lineWh = it.warehouse_code || '';
          if (lineWh && lineWh !== headerWh) return it;
          const { warehouse_code, ...rest } = it;
          void warehouse_code;
          return rest;
        }),
      };
      setWarning(null);
      const created = await (isSplit ? createGoodsReceiptsSplit(payload) : createGoodsReceipt(payload));
      setSuccess(isSplit ? 'Goods receipts created (split by warehouse)' : 'Goods receipt created successfully');
      setWarning(skippedCostRowsWarning(skippedCostRows(created), 'Goods receipt recorded'));
      setFormTouched(false); // saved — closing is safe now
      setDialogOpen(false);
      loadReceipts();
    } catch (err) {
      setError(extractApiError(err) || 'Failed to create goods receipt');
    }
  };

  const handleAccept = async (receiptId) => {
    try {
      await acceptGoodsReceipt(receiptId);
      setSuccess('Goods receipt accepted');
      loadReceiptDetails(receiptId);
    } catch (err) {
      setError(extractApiError(err) || 'Failed to accept goods receipt');
    }
  };

  const handleSaveReceiptDate = async () => {
    const current = String(selectedReceipt?.receipt_date || '').split('T')[0];
    if (!receiptDateDraft || receiptDateDraft === current) {
      setEditingReceiptDate(false);
      return;
    }
    try {
      setSavingReceiptDate(true);
      setError(null);
      await updateGoodsReceipt(selectedReceipt.id, { receipt_date: receiptDateDraft });
      setSuccess('Receipt date updated — batch dates synced');
      setEditingReceiptDate(false);
      loadReceiptDetails(selectedReceipt.id);
      loadReceipts();
    } catch (err) {
      setError(extractApiError(err) || 'Failed to update receipt date');
    } finally {
      setSavingReceiptDate(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }
    try {
      await rejectGoodsReceipt(selectedReceipt.id, rejectReason);
      setSuccess('Goods receipt rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
      loadReceiptDetails(selectedReceipt.id);
    } catch (err) {
      // 409 with blocking_transfer: backend identified an in-transit transfer
      // still drawing from this GR's batches. Splice the transfer number out
      // of the human-readable message and render it as a router link so the
      // operator can click straight through to cancel/delete it.
      const bt = err?.response?.data?.blocking_transfer;
      const msg = extractApiError(err) || 'Failed to reject goods receipt';
      if (bt && bt.transfer_number && bt.id && msg.includes(bt.transfer_number)) {
        const [before, after] = msg.split(bt.transfer_number);
        setError(
          <span>
            {before}
            <RouterLink to={`/inventory/transfers/${bt.id}`} style={{ fontWeight: 600 }}>
              {bt.transfer_number}
            </RouterLink>
            {after}
          </span>
        );
      } else {
        setError(msg);
      }
    }
  };

  // Hard-delete a GR. Server-side guards in deleteGoodsReceipt prevent delete
  // when inspection is approved/completed or when inventory_batches from this
  // GR have been consumed/reserved. Stale UI state can still attempt the
  // delete — we just surface the 409 error from the API.
  const handleDelete = async () => {
    if (!selectedReceipt?.id) return;
    const grLabel = selectedReceipt.grn_number || selectedReceipt.gr_number || `#${selectedReceipt.id}`;
    const ok = await confirm({
      title: `Delete goods receipt ${grLabel}?`,
      message:
        'The received quantity is rolled back on the purchase order and any stock batches created by this receipt are removed.\n\n'
        + 'This only works for a receipt that is still pending or rejected and whose stock has not been used. It cannot be undone.',
      confirmText: 'Delete receipt',
      severity: 'danger',
    });
    if (!ok) return;
    try {
      await deleteGoodsReceipt(selectedReceipt.id);
      setSuccess(`Goods receipt ${grLabel} deleted`);
      // Navigate back to the list; the detail no longer exists.
      navigate('/procurement/goods-receipts');
      loadReceipts();
    } catch (err) {
      setError(extractApiError(err) || 'Failed to delete goods receipt');
    }
  };

  // Search + status filter are now server-side; no client-side filtering needed.
  // Kept the alias `filteredReceipts = receipts` so downstream JSX continues to
  // compile without touching every reference.
  const filteredReceipts = receipts;

  // GR-5 document upload/delete — docType is 'commercial_invoice' or 'packing_list'.
  // File comes from a hidden <input type="file"> per slot; backend enforces 25MB +
  // PDF/PNG/JPG. On success we refresh the detail to surface the new URL.
  const [uploadingDoc, setUploadingDoc] = useState(null); // docType currently being uploaded

  // List-page shortcuts: `n` creates new GR when on the list page.
  useListShortcuts({
    onNew: () => { if (!id) { setError(null); setDialogOpen(true); } },
    onRefresh: () => { if (!id) loadReceipts(); },
  });
  const handleUploadDocument = async (docType, file) => {
    if (!file || !selectedReceipt?.id) return;
    setUploadingDoc(docType);
    try {
      await uploadGoodsReceiptDocument(selectedReceipt.id, docType, file);
      setSuccess(`${docType === 'commercial_invoice' ? 'Commercial invoice' : 'Packing list'} uploaded`);
      await loadReceiptDetails(selectedReceipt.id);
    } catch (err) {
      setError(extractApiError(err) || 'Failed to upload document');
    } finally {
      setUploadingDoc(null);
    }
  };
  const handleDeleteDocument = async (docType) => {
    if (!selectedReceipt?.id) return;
    const docLabel = docType === 'commercial_invoice' ? 'commercial invoice' : 'packing list';
    const ok = await confirm({
      title: `Remove the ${docLabel}?`,
      message: 'The document is removed from this goods receipt. You can upload it again afterwards.',
      confirmText: 'Remove document',
      severity: 'danger',
    });
    if (!ok) return;
    try {
      await deleteGoodsReceiptDocument(selectedReceipt.id, docType);
      setSuccess('Document removed');
      await loadReceiptDetails(selectedReceipt.id);
    } catch (err) {
      setError(extractApiError(err) || 'Failed to remove document');
    }
  };

  // The DB stores an /api/... URL but a plain <a href> can't attach the Bearer
  // token, so we fetch via the authed axios client, build a blob URL, and open
  // that in a new tab. URL gets revoked on tab close (browser handles it).
  const handleOpenDocument = async (url) => {
    if (!url) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(`Failed to open document: ${err.message}`);
    }
  };

  // GRN PDF download — fetches the SYSTEM-generated Goods Receipt Note PDF
  // (distinct from the supplier-uploaded CI / Packing List slots above).
  // Uses the axios `api` client so the Bearer token is attached automatically.
  const [grnDownloading, setGrnDownloading] = useState(false);
  const handleDownloadGRN = async () => {
    if (!selectedReceipt?.id) return;
    setGrnDownloading(true);
    try {
      const grn = encodeURIComponent(selectedReceipt.grn_number || `GRN-${selectedReceipt.id}`);
      const resp = await api.get(`/api/procurement/goods-receipts/${selectedReceipt.id}/pdf?grn_number=${grn}`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = blobUrl;
      a.download = `${selectedReceipt.grn_number || `GRN-${selectedReceipt.id}`}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      setSuccess('GRN downloaded');
      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      setError(extractApiError(err) || 'Failed to generate GRN PDF');
    } finally {
      setGrnDownloading(false);
    }
  };

  // Inspection edit-mode handlers
  const openInspectionEdit = () => {
    if (!selectedReceipt?.items) return;
    const seed = {};
    selectedReceipt.items.forEach(it => {
      const itemId = it.id || it.gr_item_id;
      if (!itemId) return;
      const received = Number(it.received_quantity) || 0;
      const accepted = Number(it.accepted_quantity) || 0;
      const rejected = Number(it.rejected_quantity) || 0;
      // Default Accepted = Received when both accepted + rejected are zero
      // (operator typical: "everything came in good"). Doesn't override real
      // values — only fills the blank inspection case where accepted=0+rejected=0.
      const seedAccepted = (accepted === 0 && rejected === 0 && received > 0)
        ? received
        : accepted;
      seed[itemId] = {
        accepted_quantity: seedAccepted,
        rejected_quantity: rejected,
        damage_reason: it.damage_reason || '',
      };
    });
    setInspectionEdits(seed);
    setInspectionEditMode(true);
  };
  const updateInspectionLine = (itemId, field, rawValue) => {
    const it = (selectedReceipt.items || []).find(x => (x.id || x.gr_item_id) === itemId);
    if (!it) return;
    const next = { ...(inspectionEdits[itemId] || {}) };
    // "Live" received: edited value if present, else the saved value.
    const liveReceived = next.received_quantity !== undefined
      ? Number(next.received_quantity) || 0
      : Number(it.received_quantity) || 0;
    if (field === 'received_quantity') {
      const v = Math.max(0, Number(rawValue) || 0);
      next.received_quantity = v;
      // Auto-recompute Accepted = Received − Rejected on every Received edit.
      // This is the operator's mental model ("everything came in good unless
      // I marked some rejected"), so keep Accepted in lockstep with Received
      // instead of silently leaving it at the prior value. Rejected stays
      // sticky (operator explicitly entered it); we just clamp it down if
      // it would exceed the new received.
      const curRej = next.rejected_quantity !== undefined ? Number(next.rejected_quantity) : Number(it.rejected_quantity || 0);
      const newRej = Math.min(curRej, v);
      next.rejected_quantity = newRej;
      next.accepted_quantity = Math.max(0, v - newRej);
    } else if (field === 'accepted_quantity') {
      const v = Math.max(0, Math.min(liveReceived, Number(rawValue) || 0));
      next.accepted_quantity = v;
      next.rejected_quantity = Math.max(0, liveReceived - v);
    } else if (field === 'rejected_quantity') {
      const v = Math.max(0, Math.min(liveReceived, Number(rawValue) || 0));
      next.rejected_quantity = v;
      next.accepted_quantity = Math.max(0, liveReceived - v);
    } else {
      next[field] = rawValue;
    }
    setInspectionEdits(prev => ({ ...prev, [itemId]: next }));
  };
  const saveInspection = async () => {
    setSavingInspection(true);
    setError(null);
    try {
      // Did any line change received_quantity? If so, route to the full-edit
      // endpoint (updateGoodsReceipt) which handles all three quantity fields
      // + over-receipt guard + invariant check. Otherwise fall back to the
      // narrower updateQualityInspection which also stamps inspection_status
      // ='completed' — preserves the historical "Save Inspection" semantic
      // for the QA-only flow.
      const itemsPayload = (selectedReceipt.items || [])
        .map(it => {
          const itemId = it.id || it.gr_item_id;
          const edits = inspectionEdits[itemId] || {};
          return {
            gr_item_id: itemId,
            received_quantity: edits.received_quantity ?? Number(it.received_quantity) ?? 0,
            accepted_quantity: edits.accepted_quantity ?? Number(it.accepted_quantity) ?? 0,
            rejected_quantity: edits.rejected_quantity ?? Number(it.rejected_quantity) ?? 0,
            damage_reason: edits.damage_reason ?? it.damage_reason ?? '',
            // Track whether received changed so the diff is unambiguous below.
            _receivedChanged: edits.received_quantity !== undefined &&
              Number(edits.received_quantity) !== Number(it.received_quantity || 0),
          };
        })
        .filter(p => p.gr_item_id);
      const anyReceivedChanged = itemsPayload.some(p => p._receivedChanged);
      const cleanPayload = itemsPayload.map(({ _receivedChanged, ...rest }) => rest);
      if (anyReceivedChanged) {
        await updateGoodsReceipt(selectedReceipt.id, { items: cleanPayload });
        setSuccess('Receipt quantities saved');
      } else {
        await updateQualityInspection(selectedReceipt.id, { items: cleanPayload });
        setSuccess('Inspection saved');
      }
      setInspectionEditMode(false);
      setInspectionEdits({});
      await loadReceiptDetails(selectedReceipt.id);
    } catch (err) {
      setError(extractApiError(err) || 'Failed to save');
    } finally {
      setSavingInspection(false);
    }
  };
  const cancelInspectionEdit = () => {
    setInspectionEditMode(false);
    setInspectionEdits({});
  };

  // Batch & expiry edit handlers
  const openBatchExpiryEdit = () => {
    const seed = {};
    (selectedReceipt.items || []).forEach(it => {
      const itemId = it.id || it.gr_item_id;
      seed[itemId] = {
        batch_number: it.batch_number || '',
        manufacturing_date: String(it.manufacturing_date || '').split('T')[0],
        expiry_date: String(it.expiry_date || '').split('T')[0],
      };
    });
    setBatchExpiryEdits(seed);
    setError(null); setBatchExpiryOpen(true);
  };
  const changeBatchExpiryLine = (itemId, field, value) => {
    setBatchExpiryEdits(prev => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), [field]: value } }));
  };
  const saveBatchExpiry = async () => {
    setSavingBatchExpiry(true);
    setError(null);
    try {
      // Send only lines whose batch or expiry actually changed (and are non-empty —
      // this flow sets/corrects, it does not clear).
      const items = (selectedReceipt.items || []).map(it => {
        const itemId = it.id || it.gr_item_id;
        const edit = batchExpiryEdits[itemId] || {};
        const curBatch = it.batch_number || '';
        const curExpiry = String(it.expiry_date || '').split('T')[0];
        const curMfg = String(it.manufacturing_date || '').split('T')[0];
        const line = { gr_item_id: itemId };
        let changed = false;
        const nextBatch = (edit.batch_number || '').trim();
        if (nextBatch !== '' && nextBatch !== curBatch) { line.batch_number = nextBatch; changed = true; }
        const nextMfg = (edit.manufacturing_date || '').trim();
        if (nextMfg !== '' && nextMfg !== curMfg) { line.manufacturing_date = nextMfg; changed = true; }
        const nextExpiry = (edit.expiry_date || '').trim();
        if (nextExpiry !== '' && nextExpiry !== curExpiry) { line.expiry_date = nextExpiry; changed = true; }
        return changed ? line : null;
      }).filter(Boolean);
      if (items.length === 0) {
        setBatchExpiryOpen(false);
        return;
      }
      await updateGoodsReceiptBatchExpiry(selectedReceipt.id, { items });
      setSuccess('Batch / expiry updated');
      setBatchExpiryOpen(false);
      setBatchExpiryEdits({});
      await loadReceiptDetails(selectedReceipt.id);
    } catch (err) {
      setError(extractApiError(err) || 'Failed to update batch / expiry');
    } finally {
      setSavingBatchExpiry(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Detail View
  if (id && selectedReceipt) {
    return (
      <Box>
        <PageHeader
          backTo="/procurement/goods-receipts"
          primary={selectedReceipt.supplier_name || (selectedReceipt.po_number ? `PO ${selectedReceipt.po_number}` : 'Goods Receipt')}
          slug={selectedReceipt.grn_number || selectedReceipt.gr_number}
          metadata={[
            ...(selectedReceipt.po_number ? [{ label: `PO ${selectedReceipt.po_number}` }] : []),
            ...(selectedReceipt.warehouse_code ? [{
              label: selectedReceipt.warehouse_name || selectedReceipt.warehouse_code,
              tooltip: selectedReceipt.warehouse_name
                ? `${selectedReceipt.warehouse_code}${selectedReceipt.warehouse_city ? ` · ${selectedReceipt.warehouse_city}` : ''}`
                : 'Unmapped warehouse code',
            }] : []),
          ]}
          statusBadge={<StatusBadge status={selectedReceipt.inspection_status || 'pending'} size="medium" />}
          actions={
            <ActionBar
              primaryCount={selectedReceipt.po_id ? 2 : 1}
              actions={[
                // Rishabh 2026-06-22: after recording/approving a receipt from a PO,
                // the header back-arrow returns to the goods-receipts list (its origin
                // when reached from that tab). Add an explicit "Back to PO" action so
                // operators who came from the PO can jump straight back to it.
                ...(selectedReceipt.po_id ? [{
                  key: 'back-to-po',
                  label: 'Back to PO',
                  icon: <BackIcon fontSize="small" />,
                  onClick: () => navigate(`/procurement/purchase-orders/${selectedReceipt.po_id}/edit`),
                }] : []),
                {
                  key: 'print',
                  label: 'Print',
                  icon: <PrintIcon fontSize="small" />,
                  onClick: () => window.print(),
                },
                {
                  key: 'copy-grn',
                  label: 'Copy GRN',
                  icon: <CopyIcon fontSize="small" />,
                  onClick: () => navigator.clipboard?.writeText(selectedReceipt.grn_number || ''),
                },
                {
                  key: 'email-supplier',
                  label: 'Email supplier',
                  icon: <EmailIcon fontSize="small" />,
                  disabled: !selectedReceipt.supplier_name,
                  onClick: () => {
                    const subject = encodeURIComponent(`Goods Receipt ${selectedReceipt.grn_number || ''}`);
                    const body = encodeURIComponent(`Hi,\n\nRegarding goods receipt ${selectedReceipt.grn_number || ''}${selectedReceipt.po_number ? ` (PO ${selectedReceipt.po_number})` : ''}.\n\nThanks,`);
                    window.location.href = `mailto:?subject=${subject}&body=${body}`;
                  },
                },
              ]}
            />
          }
        />

        {/* GR-6 inspection workflow stepper — Pending → Inspected → Finalized.
            Custom StepIcon so the final step renders true success-green when
            approved and true error-red when rejected (MUI default Stepper
            ignores StepIconProps.sx for completed steps). */}
        {(() => {
          const status = selectedReceipt.inspection_status || 'pending';
          const stepFromStatus = status === 'pending' ? 0
            : status === 'completed' ? 1
            : 2; // approved or rejected
          const isRejected = status === 'rejected';
          const isApproved = status === 'approved';
          const isInspected = status === 'completed';
          const steps = [
            { key: 'pending', label: 'Received', sub: selectedReceipt.receipt_date ? formatDate(selectedReceipt.receipt_date) : null, Icon: PendingActionsIcon },
            { key: 'inspected', label: 'Inspected', sub: selectedReceipt.inspection_date ? formatDate(selectedReceipt.inspection_date) : (isInspected || stepFromStatus >= 1 ? 'Awaiting acceptance' : 'Not yet inspected'), Icon: AssignmentTurnedInIcon },
            { key: 'finalized', label: isRejected ? 'Rejected' : isApproved ? 'Accepted' : 'Finalize', sub: isRejected ? (selectedReceipt.inspection_notes || 'Rejected') : isApproved ? 'In inventory' : 'Pending decision', Icon: isRejected ? CancelIcon : CheckCircleIcon },
          ];
          const stepIconRenderer = (idx) => (props) => {
            const { completed, active } = props;
            const isLast = idx === 2;
            const StepIco = steps[idx].Icon;
            const color = isLast && isRejected ? 'error.main'
              : isLast && isApproved ? 'success.main'
              : completed ? 'success.main'
              : active ? 'primary.main'
              : 'grey.300';
            const fontSize = active ? 32 : 28;
            return (
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color, transition: 'color 200ms ease',
              }}>
                <StepIco sx={{ fontSize }} />
              </Box>
            );
          };
          return (
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ pt: 2, pb: '12px !important' }}>
                <Stepper activeStep={stepFromStatus} alternativeLabel>
                  {steps.map((s, idx) => {
                    const reached = idx <= stepFromStatus;
                    const isLast = idx === 2;
                    return (
                      <Step key={s.key} completed={reached && !(isLast && stepFromStatus < 2)}>
                        <StepLabel
                          StepIconComponent={stepIconRenderer(idx)}
                          error={isLast && isRejected}
                        >
                          <Typography variant="body2" fontWeight={reached ? 600 : 400} color={reached ? 'text.primary' : 'text.disabled'}>
                            {s.label}
                          </Typography>
                          {s.sub && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontSize: 11 }}>
                              {s.sub}
                            </Typography>
                          )}
                        </StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>
              </CardContent>
            </Card>
          );
        })()}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {warning && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>
            {warning}
          </Alert>
        )}

        {/* Action Buttons. Accept/Reject are pre-inspection actions (status='pending').
            Void is allowed in any non-rejected state — server-side guards
            additionally refuse if any inventory_batches from this GR have been
            consumed/reserved (NetSuite/SAP-style safety). Void rolls back the
            PO qty + tears down batches; the GR row stays as 'rejected' for
            audit. Delete is hard-removal (pending/rejected only). */}
        {/* Action card. Pending → Accept / Reject / Delete. Approved → Void.
            Rejected → Delete only (lets operator clean up voided receipts off
            the list — backend allows hard-delete on pending|rejected since no
            stock can have moved past the void). */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {selectedReceipt.inspection_status === 'pending' && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckIcon />}
                  onClick={() => handleAccept(selectedReceipt.id)}
                >
                  Accept Receipt
                </Button>
              )}
              {selectedReceipt.inspection_status !== 'rejected' && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={() => { setError(null); setRejectDialogOpen(true); }}
                  title="Void this receipt: rolls back the PO qty + status, tears down any inventory batches it created. Refuses if stock has been issued/reserved."
                >
                  {selectedReceipt.inspection_status === 'pending' ? 'Reject Receipt' : 'Void Receipt'}
                </Button>
              )}
              {['pending', 'rejected'].includes(selectedReceipt.inspection_status) && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDelete}
                  sx={{ ml: 'auto' }}
                  title={selectedReceipt.inspection_status === 'rejected'
                    ? 'Permanently remove this voided receipt from the list. Inventory was already torn down at void; this just clears the audit row.'
                    : 'Hard-delete this receipt and roll back its effect on the PO. Allowed only when inspection is pending and no stock has been consumed.'}
                >
                  Delete Receipt
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Receipt Details — items table with thumbnail rail + customs cols.
            Inline-editable accept/reject + damage_reason when status is
            'pending' or 'approved' (operator can still adjust until 'completed'). */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight="600">Received Items</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(selectedReceipt.items || []).length} line{(selectedReceipt.items || []).length === 1 ? '' : 's'} ·
                      {' '}
                      {(selectedReceipt.items || []).reduce((s, it) => s + Number(it.received_quantity || 0), 0).toLocaleString()} units received ·
                      {' '}
                      <Typography component="span" color="success.dark" sx={{ fontWeight: 600 }}>
                        {(selectedReceipt.items || []).reduce((s, it) => s + Number(it.accepted_quantity || 0), 0).toLocaleString()} accepted
                      </Typography>
                      {' · '}
                      <Typography component="span" color="error.dark" sx={{ fontWeight: 600 }}>
                        {(selectedReceipt.items || []).reduce((s, it) => s + Number(it.rejected_quantity || 0), 0).toLocaleString()} rejected
                      </Typography>
                    </Typography>
                  </Box>
                  {/* Inspection edit toggle / save bar — gated to non-rejected status so a
                      rejected receipt can't have its line accepts/damage modified. */}
                  {selectedReceipt.inspection_status !== 'rejected' && (
                    inspectionEditMode ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" onClick={cancelInspectionEdit} disabled={savingInspection}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={saveInspection} disabled={savingInspection}>
                          {savingInspection ? 'Saving…' : 'Save Inspection'}
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={openBatchExpiryEdit}>
                          Batch &amp; Expiry
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={openInspectionEdit}>
                          Edit Inspection
                        </Button>
                      </Box>
                    )
                  )}
                </Box>
                <TableContainer sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { height: 8 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 4 } }}>
                  <Table size="small" sx={{ minWidth: 880 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 56 }}></TableCell>
                        <TableCell sx={{ minWidth: 260 }}>SKU · Description</TableCell>
                        <TableCell>HSN</TableCell>
                        <TableCell>COO</TableCell>
                        <TableCell>Batch</TableCell>
                        <TableCell align="right">Ordered</TableCell>
                        <TableCell align="right">Received</TableCell>
                        <TableCell align="right">Accepted</TableCell>
                        <TableCell align="right">Rejected</TableCell>
                        <TableCell>Damage Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(selectedReceipt.items || []).map((item, index) => {
                        const itemId = item.id || item.gr_item_id;
                        const edited = inspectionEditMode ? (inspectionEdits[itemId] || {}) : {};
                        const accepted = inspectionEditMode ? (edited.accepted_quantity ?? Number(item.accepted_quantity) ?? 0) : Number(item.accepted_quantity || 0);
                        const rejected = inspectionEditMode ? (edited.rejected_quantity ?? Number(item.rejected_quantity) ?? 0) : Number(item.rejected_quantity || 0);
                        const damageReason = inspectionEditMode ? (edited.damage_reason ?? item.damage_reason ?? '') : (item.damage_reason || '');
                        const received = inspectionEditMode ? (edited.received_quantity ?? Number(item.received_quantity) ?? 0) : Number(item.received_quantity || 0);
                        return (
                          <TableRow key={itemId || index} hover>
                            <TableCell>
                              {item.thumbnail_url || item.image_url ? (
                                <Box component="img" src={item.thumbnail_url || item.image_url}
                                  sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 0.5, border: '1px solid', borderColor: 'divider', bgcolor: 'common.white' }}
                                  onError={(e) => { e.target.style.display = 'none'; }} />
                              ) : (
                                <Box sx={{ width: 48, height: 48, borderRadius: 0.5, border: '1px dashed', borderColor: 'divider', bgcolor: 'background.default' }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight="medium" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{item.sku}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {item.product_name || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                              {item.hs_code || <span style={{ color: '#bbb' }}>—</span>}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                              {item.country_of_origin || <span style={{ color: '#bbb' }}>—</span>}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                              {item.batch_number ? (
                                <Chip label={item.batch_number} size="small" variant="outlined" sx={{ fontSize: 10, height: 20, '& .MuiChip-label': { px: 0.75 } }} />
                              ) : <span style={{ color: '#bbb' }}>—</span>}
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{formatNumber(item.ordered_quantity)}</TableCell>
                            <TableCell align="right">
                              {inspectionEditMode ? (
                                <TextField type="number" size="small"
                                  inputProps={{ min: 0, style: { textAlign: 'right', padding: '4px 6px', fontFamily: 'monospace', fontSize: 12 } }}
                                  value={received}
                                  onChange={(e) => updateInspectionLine(itemId, 'received_quantity', e.target.value)}
                                  sx={{ width: 80 }} />
                              ) : (
                                <Typography sx={{ fontFamily: 'monospace' }}>{formatNumber(received)}</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {inspectionEditMode ? (
                                <TextField type="number" size="small"
                                  inputProps={{ min: 0, max: received, style: { textAlign: 'right', padding: '4px 6px', fontFamily: 'monospace', fontSize: 12 } }}
                                  value={accepted}
                                  onChange={(e) => updateInspectionLine(itemId, 'accepted_quantity', e.target.value)}
                                  sx={{ width: 80 }} />
                              ) : (
                                <Typography color="success.main" fontWeight="medium" sx={{ fontFamily: 'monospace' }}>
                                  {formatNumber(accepted)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {inspectionEditMode ? (
                                <TextField type="number" size="small"
                                  inputProps={{ min: 0, max: received, style: { textAlign: 'right', padding: '4px 6px', fontFamily: 'monospace', fontSize: 12 } }}
                                  value={rejected}
                                  onChange={(e) => updateInspectionLine(itemId, 'rejected_quantity', e.target.value)}
                                  sx={{ width: 80 }} />
                              ) : rejected > 0 ? (
                                <Typography color="error.main" fontWeight="medium" sx={{ fontFamily: 'monospace' }}>
                                  {formatNumber(rejected)}
                                </Typography>
                              ) : (
                                <Typography color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: 12 }}>0</Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 240 }}>
                              {inspectionEditMode ? (
                                <TextField size="small" fullWidth
                                  placeholder={rejected > 0 ? 'Required when rejecting' : 'Optional'}
                                  value={damageReason}
                                  onChange={(e) => updateInspectionLine(itemId, 'damage_reason', e.target.value)}
                                  inputProps={{ style: { padding: '4px 6px', fontSize: 12 } }} />
                              ) : damageReason ? (
                                <Tooltip title={damageReason} arrow>
                                  <Typography variant="caption" sx={{ color: 'error.dark', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', cursor: 'help', borderBottom: '1px dotted', borderColor: 'error.light' }}>
                                    {damageReason}
                                  </Typography>
                                </Tooltip>
                              ) : <span style={{ color: '#bbb' }}>—</span>}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="600" gutterBottom>Receipt Details</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography color="textSecondary">Receipt Date</Typography>
                    {editingReceiptDate ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField
                          size="small" type="date" value={receiptDateDraft}
                          onChange={(e) => setReceiptDateDraft(e.target.value)}
                          inputProps={{ max: new Date().toISOString().split('T')[0], style: { padding: '4px 6px', fontSize: 13 } }}
                        />
                        <IconButton size="small" color="success" onClick={handleSaveReceiptDate} disabled={savingReceiptDate}>
                          {savingReceiptDate ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                        </IconButton>
                        <IconButton size="small" onClick={() => setEditingReceiptDate(false)} disabled={savingReceiptDate}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography fontWeight="medium">{formatDate(selectedReceipt.receipt_date)}</Typography>
                        <Tooltip title="Edit receipt date — also corrects this receipt's batch dates" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setReceiptDateDraft(String(selectedReceipt.receipt_date || '').split('T')[0]);
                              setEditingReceiptDate(true);
                            }}
                          >
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                  {selectedReceipt.po_number && selectedReceipt.po_id && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="textSecondary">PO</Typography>
                      <Typography
                        fontWeight="medium"
                        sx={{ fontFamily: 'monospace', fontSize: 13, color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => navigate(`/procurement/purchase-orders/${selectedReceipt.po_id}/edit`)}
                      >
                        {selectedReceipt.po_number}
                      </Typography>
                    </Box>
                  )}
                  {selectedReceipt.warehouse_code && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Typography color="textSecondary">Warehouse</Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        <EntityChip
                          code={selectedReceipt.warehouse_code}
                          name={selectedReceipt.warehouse_name}
                          subtitle={selectedReceipt.warehouse_city || null}
                          variant="stacked"
                        />
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">Received By</Typography>
                    <Typography fontWeight="medium" sx={{ fontSize: 13 }}>
                      {selectedReceipt.created_by_name || selectedReceipt.received_by_name || '—'}
                    </Typography>
                  </Box>
                  {(selectedReceipt.inspector_name || selectedReceipt.inspected_by_name) && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="textSecondary">Inspected By</Typography>
                      <Typography fontWeight="medium" sx={{ fontSize: 13 }}>
                        {selectedReceipt.inspector_name || selectedReceipt.inspected_by_name}
                      </Typography>
                    </Box>
                  )}
                  {selectedReceipt.inspection_date && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="textSecondary">Inspection Date</Typography>
                      <Typography fontWeight="medium">{formatDate(selectedReceipt.inspection_date)}</Typography>
                    </Box>
                  )}
                </Box>

                {/* GR-5 Documents — drag-drop zones for Commercial Invoice + Packing List.
                    Empty: tall dashed dropzone w/ icon + cta. Populated: compact filled card
                    showing filename + actions. Both states accept drag-drop OR click-to-pick. */}
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  {/* T1.3: system-generated GRN at the top, visually separated
                      from the supplier-doc upload slots below. The two sections
                      have distinct meaning — "what we generated to confirm
                      receipt" vs "what the supplier sent us". Operators were
                      asking why there was only Upload (no Download) when the
                      system clearly knows what was received. */}
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', mb: 1 }}>
                    Our document (generated)
                  </Typography>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, mb: 2,
                    border: '1px solid', borderColor: 'primary.light',
                    bgcolor: 'action.hover', borderRadius: 1.5,
                  }}>
                    <DocIcon sx={{ fontSize: 22, color: 'primary.main' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                        Goods Receipt Note (GRN)
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Auto-generated from this receipt — ordered vs received vs accepted per line
                      </Typography>
                    </Box>
                    <Button size="small" variant="contained"
                      startIcon={grnDownloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon sx={{ fontSize: 16 }} />}
                      disabled={grnDownloading}
                      onClick={handleDownloadGRN}
                      sx={{ textTransform: 'none' }}>
                      {grnDownloading ? 'Generating…' : 'Download GRN'}
                    </Button>
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', mb: 1 }}>
                    Supplier documents (uploaded)
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 1.25 }}>
                    {[
                      { type: 'commercial_invoice', label: 'Commercial Invoice', url: selectedReceipt.commercial_invoice_url },
                      { type: 'packing_list', label: 'Packing List', url: selectedReceipt.packing_list_url },
                    ].map(slot => {
                      const isUploading = uploadingDoc === slot.type;
                      const hasFile = Boolean(slot.url);
                      return hasFile ? (
                        <Box key={slot.type}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25,
                            border: '1px solid', borderColor: 'success.light',
                            bgcolor: 'success.lighter', borderRadius: 1.5,
                          }}>
                          <DocIcon sx={{ fontSize: 22, color: 'success.main' }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>{slot.label}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Uploaded</Typography>
                          </Box>
                          <Button size="small" onClick={() => handleOpenDocument(slot.url)} sx={{ minWidth: 0, textTransform: 'none', fontWeight: 600 }}>
                            Open ↗
                          </Button>
                          <Button size="small" component="label"
                            startIcon={isUploading ? <CircularProgress size={14} /> : <UploadIcon sx={{ fontSize: 16 }} />}
                            disabled={isUploading}
                            sx={{ textTransform: 'none' }}>
                            Replace
                            <input type="file" hidden
                              accept="application/pdf,image/png,image/jpeg"
                              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadDocument(slot.type, f); e.target.value = ''; }} />
                          </Button>
                          <Tooltip title="Remove">
                            <IconButton size="small" color="error" onClick={() => handleDeleteDocument(slot.type)} disabled={isUploading}>
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <UploadZone
                          key={slot.type}
                          id={`gr-doc-${slot.type}`}
                          label={slot.label}
                          hint="Drag a file here, or click to browse"
                          accept="application/pdf,image/png,image/jpeg"
                          uploading={isUploading}
                          onFile={(f) => handleUploadDocument(slot.type, f)}
                        />
                      );
                    })}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: 11 }}>
                    PDF, PNG, or JPG · Max 25 MB
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {selectedReceipt.notes && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="600" gutterBottom>Notes</Typography>
                  <Typography>{selectedReceipt.notes}</Typography>
                </CardContent>
              </Card>
            )}

            {/* Activity timeline — audit_log entries with humanized actions + actor names. */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>Activity</Typography>
                <Timeline
                  events={timelineEvents.map(e => ({
                    ...e,
                    timestamp: e.timestamp ? formatDate(e.timestamp) + ' ' + (String(e.timestamp).split(' ')[1] || '').slice(0, 5) : '',
                  }))}
                  loading={timelineLoading}
                  empty={
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No activity recorded yet.
                    </Typography>
                  }
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Batch & Expiry edit dialog — add/correct batch number + expiry per line. */}
        <Dialog open={batchExpiryOpen} onClose={() => setBatchExpiryOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit batch, manufacturing date &amp; expiry</DialogTitle>
          <DialogContent dividers>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add or correct the batch/lot number, manufacturing date and expiry per line — including the
              finished-good (FG) batch on a production receipt. Dates can be changed anytime; the batch
              number can only be changed while that batch's stock hasn't been issued, reserved, or transferred.
            </Typography>
            {(selectedReceipt?.items || []).map((it) => {
              const itemId = it.id || it.gr_item_id;
              const edit = batchExpiryEdits[itemId] || {};
              // For a manufacture build the FG batch lives under the produced (finished-good)
              // SKU, which differs from the bulk-fill line SKU — show it so the operator edits
              // the right thing.
              const displaySku = it.produced_sku && it.produced_sku !== it.sku
                ? `${it.produced_sku} (FG · built from ${it.sku})`
                : it.sku;
              return (
                <Box key={itemId} sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{displaySku}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {it.product_name || '—'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField label="Batch Number" size="small" sx={{ flex: '1 1 100%' }}
                      value={edit.batch_number || ''}
                      onChange={(e) => changeBatchExpiryLine(itemId, 'batch_number', e.target.value)} />
                    <TextField label="Manufacturing Date" type="date" size="small" sx={{ flex: '1 1 45%' }}
                      InputLabelProps={{ shrink: true }}
                      value={edit.manufacturing_date || ''}
                      onChange={(e) => changeBatchExpiryLine(itemId, 'manufacturing_date', e.target.value)} />
                    <TextField label="Expiry Date" type="date" size="small" sx={{ flex: '1 1 45%' }}
                      InputLabelProps={{ shrink: true }}
                      value={edit.expiry_date || ''}
                      onChange={(e) => changeBatchExpiryLine(itemId, 'expiry_date', e.target.value)} />
                  </Box>
                </Box>
              );
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBatchExpiryOpen(false)} disabled={savingBatchExpiry}>Cancel</Button>
            <Button variant="contained" onClick={saveBatchExpiry} disabled={savingBatchExpiry}>
              {savingBatchExpiry ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Reject Goods Receipt</DialogTitle>
          <DialogContent>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <Typography gutterBottom>
              Please provide a reason for rejecting this goods receipt.
            </Typography>
            <TextField
              label="Rejection Reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              required
              fullWidth
              multiline
              rows={3}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleReject}>
              Reject
            </Button>
          </DialogActions>
        </Dialog>

        {/* Shared confirm dialog (delete receipt / remove document) */}
        <ConfirmDialog {...confirmDialogProps} />
      </Box>
    );
  }

  // List View
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Goods Receipts
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Record and manage incoming goods from purchase orders — company-wide, across all brands
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <SavedViews
            storageKey="gr-list"
            currentState={{ searchTerm, statusFilter }}
            onApply={(s) => {
              setSearchTerm(s.searchTerm || '');
              setStatusFilter(s.statusFilter || '');
              setPage(0);
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setError(null); setDialogOpen(true); }}
          >
            New Receipt
          </Button>
          <Tooltip title="Refresh (r)">
            <IconButton onClick={loadReceipts}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {warning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>
          {warning}
        </Alert>
      )}

      {/* Search + status filter chips */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search GR# / PO# / supplier…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { setSearchTerm(searchInput); setPage(0); }
              if (e.key === 'Escape') { setSearchInput(''); setSearchTerm(''); setPage(0); }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
            size="small"
            sx={{ width: 360 }}
          />
          {/* Status filter chips — driven by inspection_status (the actual
              column name; the prior code used 'status' which doesn't exist
              and rendered an empty chip silently). */}
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {[
              { label: 'All', value: '' },
              { label: 'Pending', value: 'pending' },
              { label: 'Approved', value: 'approved' },
              { label: 'Completed', value: 'completed' },
              { label: 'Rejected', value: 'rejected' },
            ].map(opt => (
              <Chip
                key={opt.value || 'all'}
                label={opt.label}
                size="small"
                clickable
                onClick={() => { setStatusFilter(opt.value); setPage(0); }}
                color={statusFilter === opt.value ? 'primary' : 'default'}
                variant={statusFilter === opt.value ? 'filled' : 'outlined'}
              />
            ))}
            <Chip
              label="Show drafts & opening balances"
              size="small"
              clickable
              onClick={() => { setShowNonStock(v => !v); setPage(0); }}
              color={showNonStock ? 'primary' : 'default'}
              variant={showNonStock ? 'filled' : 'outlined'}
              title="Include receipts that do not affect stock: factory-import drafts and CIN7 opening balances"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Receipts Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>GR Number</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>PO #</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Warehouse</TableCell>
                  <TableCell align="right">Lines</TableCell>
                  <TableCell align="right">Recv'd Qty</TableCell>
                  <TableCell>Receipt Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReceipts.map((receipt) => {
                  const statusLabel = receipt.inspection_status || 'pending';
                  return (
                    <TableRow
                      key={receipt.id || receipt.grn_number}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/procurement/goods-receipts/${receipt.id}`)}
                    >
                      <TableCell>
                        <Box
                          component="a"
                          onClick={(e) => { e.stopPropagation(); navigate(`/procurement/goods-receipts/${receipt.id}`); }}
                          sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {receipt.grn_number}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Provenance labels mirror the backend's receipt_type
                          // (prefix-derived, live-verified). Non-stock rows get
                          // an outlined chip + tooltip so drafts are visibly
                          // different from stock-affecting receipts.
                          const t = RECEIPT_TYPE_LABELS[receipt.receipt_type] || { label: receipt.receipt_type || '—', color: 'default' };
                          const nonStock = Number(receipt.affects_stock) === 0;
                          return (
                            <Chip
                              size="small"
                              label={t.label}
                              color={t.color}
                              variant={nonStock ? 'outlined' : 'filled'}
                              title={nonStock ? `${t.label} — does not affect stock` : t.label}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {receipt.brand_name || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                      </TableCell>
                      <TableCell>
                        {receipt.po_number ? (
                          <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>{receipt.po_number}</Typography>
                        ) : <Typography color="text.disabled" sx={{ fontStyle: 'italic', fontSize: 12 }}>—</Typography>}
                      </TableCell>
                      <TableCell>{receipt.supplier_name || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}</TableCell>
                      <TableCell>
                        {receipt.warehouse_code ? (
                          <EntityChip code={receipt.warehouse_code} name={receipt.warehouse_name} />
                        ) : <Typography color="text.disabled" sx={{ fontStyle: 'italic', fontSize: 12 }}>—</Typography>}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                        {Number(receipt.line_count || 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                        {Number(receipt.total_received_qty || 0).toLocaleString()}
                      </TableCell>
                      <TableCell>{formatDate(receipt.receipt_date)}</TableCell>
                      <TableCell>
                        <StatusBadge status={statusLabel} />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredReceipts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ borderBottom: 'none' }}>
                      <EmptyState
                        icon={ReceiptIcon}
                        title={searchTerm || statusFilter ? 'No matching receipts' : 'No goods receipts yet'}
                        description={searchTerm || statusFilter
                          ? 'Try clearing the search or status filter.'
                          : 'Create a goods receipt against an open purchase order to record an inbound shipment.'}
                        compact
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalReceipts}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[25, 50, 100, 250]}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Create Receipt Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => guardedCloseDialog(() => { setFormTouched(false); setDialogOpen(false); })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Record Goods Receipt</DialogTitle>
        {/* onChangeCapture marks the form dirty so closing warns first. */}
        <DialogContent onChangeCapture={() => setFormTouched(true)}>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth required>
                  <InputLabel>Purchase Order</InputLabel>
                  <Select
                    value={receiptForm.po_id}
                    label="Purchase Order"
                    onChange={(e) => handlePOChange(e.target.value)}
                  >
                    {purchaseOrders.map(po => (
                      <MenuItem key={po.id} value={po.id}>
                        {po.po_number} - {po.supplier_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label="Receipt Date"
                  type="date"
                  value={receiptForm.receipt_date}
                  onChange={(e) => setReceiptForm({ ...receiptForm, receipt_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} md={4}>
                {/* Destination warehouse for this whole receipt. Defaults from
                    PO.warehouse_code on PO select; operator can override.
                    Empty falls through to PO warehouse server-side. */}
                <FormControl fullWidth>
                  <InputLabel>Destination warehouse</InputLabel>
                  <Select
                    label="Destination warehouse"
                    value={receiptForm.warehouse_code || ''}
                    onChange={(e) => { setFormTouched(true); setReceiptForm({ ...receiptForm, warehouse_code: e.target.value }); }}
                  >
                    <MenuItem value=""><em>None — fall through to PO</em></MenuItem>
                    {warehouses.map(w => (
                      <MenuItem key={w.code} value={w.code}>
                        {w.code} — {w.name}{w.country ? ` · ${w.country}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* GIT-warehouse nudge — legacy CIN7-imported POs carry
                  warehouse_code='GIT' (an accounting bucket, not a real
                  location). loadPOForReceipt deliberately leaves the picker
                  empty in this case so the operator is forced to pick a
                  real destination. */}
              {selectedPO && String(selectedPO.warehouse_code || '').toUpperCase() === 'GIT' && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    This PO ships to <strong>GIT</strong> (Goods In Transit — accounting bucket only).
                    Pick the actual receiving warehouse above before recording the receipt.
                  </Alert>
                </Grid>
              )}
            </Grid>

            {receiptForm.items.length > 0 && (
              <>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>
                  Items to Receive
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>SKU</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Ordered</TableCell>
                        <TableCell align="right">Already received</TableCell>
                        <TableCell align="right">Remaining</TableCell>
                        <TableCell align="right">This receipt</TableCell>
                        <TableCell align="right">Rejected</TableCell>
                        <TableCell align="right">Accepted</TableCell>
                        <TableCell sx={{ minWidth: 120 }}>Batch #</TableCell>
                        <TableCell sx={{ minWidth: 150 }}>Expiry</TableCell>
                        <TableCell sx={{ minWidth: 130 }}>Warehouse</TableCell>
                        <TableCell>Rejection Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {receiptForm.items.map((item, index) => {
                        // Discrepancy flag — surface a per-row warning when
                        // received qty differs from ordered. Operator can still
                        // submit; this just makes it impossible to miss.
                        const ord = Number(item.ordered_quantity) || 0;
                        const recv = Number(item.received_quantity) || 0;
                        const variance = recv - ord;
                        const isShort = variance < 0 && ord > 0;
                        const isOver = variance > 0;
                        return (
                          <TableRow key={index} sx={
                            isShort ? { bgcolor: 'warning.lighter' } :
                            isOver ? { bgcolor: 'info.lighter' } : {}
                          }>
                            <TableCell>
                              <Typography fontWeight="medium">{item.sku}</Typography>
                            </TableCell>
                            <TableCell>{item.product_name || '—'}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{item.ordered_quantity}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                              {Number(item.already_received || 0).toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', color: (item.remaining_quantity || 0) === 0 ? 'success.main' : 'warning.dark' }}>
                              {Number(item.remaining_quantity || 0).toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                value={item.received_quantity}
                                onChange={(e) => handleUpdateItem(index, 'received_quantity', e.target.value)}
                                size="small"
                                sx={{ width: 80 }}
                                error={isShort}
                                disabled={(item.remaining_quantity || 0) === 0}
                                inputProps={{ min: 0, max: item.remaining_quantity }}
                                helperText={isShort ? `short ${Math.abs(variance)}` : (isOver ? `over ${variance}` : '')}
                                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                value={item.rejected_quantity}
                                onChange={(e) => handleUpdateItem(index, 'rejected_quantity', e.target.value)}
                                size="small"
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography color="success.main" fontWeight="medium">
                                {item.accepted_quantity}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {/* Optional supplier/production batch or lot number. Saved to the
                                  receipt line + its inventory batch (honoured on any PO type). */}
                              <TextField size="small" placeholder="optional"
                                value={item.batch_number || ''}
                                onChange={(e) => handleUpdateItem(index, 'batch_number', e.target.value)}
                                inputProps={{ style: { fontSize: 12, padding: '6px 8px' } }}
                                sx={{ width: 120 }} />
                            </TableCell>
                            <TableCell>
                              <TextField type="date" size="small"
                                value={item.expiry_date || ''}
                                onChange={(e) => handleUpdateItem(index, 'expiry_date', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ style: { fontSize: 12, padding: '6px 8px' } }}
                                sx={{ width: 150 }} />
                            </TableCell>
                            <TableCell>
                              {/* Per-line warehouse — defaults to header value
                                  ("(use header)"). Pick a different warehouse
                                  to split this receipt across destinations;
                                  server creates one Pattern-A GR per destination. */}
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={item.warehouse_code || ''}
                                  displayEmpty
                                  onChange={(e) => handleUpdateItem(index, 'warehouse_code', e.target.value)}
                                  sx={{ fontSize: 13 }}
                                >
                                  <MenuItem value=""><em>(use header)</em></MenuItem>
                                  {warehouses.map(w => (
                                    <MenuItem key={w.code} value={w.code}>{w.code}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              {item.rejected_quantity > 0 && (
                                <TextField
                                  value={item.rejection_reason}
                                  onChange={(e) => handleUpdateItem(index, 'rejection_reason', e.target.value)}
                                  placeholder="Reason for rejection"
                                  size="small"
                                  sx={{ width: 200 }}
                                  required
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Summary footer — total ordered vs total received with
                          variance flagged. Quick at-a-glance check for the operator. */}
                      {receiptForm.items.length > 1 && (() => {
                        const totalOrd = receiptForm.items.reduce((s, i) => s + (Number(i.ordered_quantity) || 0), 0);
                        const totalRecv = receiptForm.items.reduce((s, i) => s + (Number(i.received_quantity) || 0), 0);
                        const variance = totalRecv - totalOrd;
                        return (
                          <TableRow sx={{ borderTop: '2px solid', borderColor: 'divider', fontWeight: 700 }}>
                            <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{totalOrd.toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700,
                              color: variance < 0 ? 'warning.dark' : variance > 0 ? 'info.dark' : 'success.dark' }}>
                              {totalRecv.toLocaleString()}
                              {variance !== 0 && (
                                <Typography variant="caption" display="block" sx={{ fontSize: 10, fontWeight: 600 }}>
                                  {variance > 0 ? `+${variance} over` : `${variance} short`}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell colSpan={6} />
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            <TextField
              label="Notes"
              value={receiptForm.notes}
              onChange={(e) => setReceiptForm({ ...receiptForm, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => guardedCloseDialog(() => { setFormTouched(false); setDialogOpen(false); })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!receiptForm.po_id || receiptForm.items.length === 0 || !receiptForm.warehouse_code}
            title={!receiptForm.warehouse_code ? 'Pick a destination warehouse first' : ''}
          >
            Create Receipt
          </Button>
        </DialogActions>
      </Dialog>

      {/* MUST be mounted in this (list) branch too, not just the detail branch:
          the create dialog and both guardedCloseDialog call sites live here, and
          the guard awaits a promise only this component resolves. Mounted in one
          branch only, Cancel and the backdrop silently became dead controls with
          no way to close the dialog. */}
      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
}

export default GoodsReceipts;
